import React from 'react';
import { TableCell, TableRow } from '../../../components/ui/table';
import { Button } from '../../../components/ui/button';
import { Edit, Trash2, CheckCircle, XCircle } from 'lucide-react';

interface Recruitment {
  id: number;
  department_id: number;
  position_id: number;
  number_of_positions: number;
  employment_type: string;
  status: string;
  budget?: number;
  expected_start_date?: string;
}

interface RecruitmentTableRowProps {
  rec: Recruitment;
  getDepartmentName: (id: number) => string;
  getPositionName: (id: number) => string;
  onStatusChange: (rec: Recruitment, status: string) => void;
  onEdit: (rec: Recruitment) => void;
  onDelete: (id: number) => void;
  onAddOvertime?: (rec: Recruitment) => void;
  getOvertimeForRecruitment?: (recId: number) => any;
}

const statusColors = {
  draft: 'bg-gray-100 text-gray-700',
  pending: 'bg-amber-100 text-amber-700',
  approved: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700',
  in_progress: 'bg-blue-100 text-blue-700',
  completed: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-gray-100 text-gray-700',
};

const statusLabels = {
  draft: 'Draft',
  pending: 'Menunggu',
  approved: 'Disetujui',
  rejected: 'Ditolak',
  in_progress: 'Dalam Proses',
  completed: 'Selesai',
  cancelled: 'Dibatalkan',
};

const employmentLabels = {
  permanent: 'Tetap',
  contract: 'Kontrak',
  intern: 'Magang',
};

export default function RecruitmentTableRow({
  rec,
  getDepartmentName,
  getPositionName,
  onStatusChange,
  onEdit,
  onDelete,
}: RecruitmentTableRowProps) {
  const status = rec.status || 'pending';

  return (
    <TableRow className="hover:bg-gray-50">
      <TableCell>
        <p className="font-medium text-gray-900">{getDepartmentName(rec.department_id) || '-'}</p>
      </TableCell>
      <TableCell>
        <p className="text-sm text-gray-700">{getPositionName(rec.position_id) || '-'}</p>
      </TableCell>
      <TableCell>
        <p className="text-sm text-gray-900">{rec.number_of_positions} orang</p>
      </TableCell>
      <TableCell>
        <span className="inline-flex items-center bg-gray-100 text-gray-700 px-3 py-1 text-sm font-medium rounded-md">
          {employmentLabels[rec.employment_type as keyof typeof employmentLabels] ||
            rec.employment_type}
        </span>
      </TableCell>
      <TableCell>
        <span
          className={`inline-flex items-center ${statusColors[status as keyof typeof statusColors] || statusColors.pending} px-3 py-1 text-sm font-medium rounded-md`}
        >
          {statusLabels[status as keyof typeof statusLabels] || 'Menunggu'}
        </span>
      </TableCell>
      <TableCell className="text-right">
        <div className="flex justify-end gap-1">
          {status === 'pending' ? (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-green-600 hover:bg-green-50"
                onClick={() => onStatusChange(rec, 'approved')}
                title="Setujui"
              >
                <CheckCircle className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-red-600 hover:bg-red-50"
                onClick={() => onStatusChange(rec, 'rejected')}
                title="Tolak"
              >
                <XCircle className="w-4 h-4" />
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => onEdit(rec)}
                title="Edit"
              >
                <Edit className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-red-500 hover:text-red-700"
                onClick={() => onDelete(rec.id)}
                title="Hapus"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}
